// packages/supabase-client/src/authService.ts

import {
  SupabaseClient,
  AuthError,
  User,
  Session,
  Subscription,
  AuthChangeEvent,
  Provider,
} from '@supabase/supabase-js';
import { AuthErrorCode, mapAuthError } from './errorMapping';

// --- Response Type for signIn/signUp operations ---
export interface AuthOperationSuccessResponse {
  success: true;
  data: { user: User | null; session: Session | null };
  error?: null;
}

export interface AuthOperationErrorResponse {
  success: false;
  data?: null;
  error: AuthError;
  errorCode: AuthErrorCode;
  errorMessage: string;
}

export type AuthOperationResponse = AuthOperationSuccessResponse | AuthOperationErrorResponse;

// --- Response Type for logout operation ---
export interface LogoutResponse {
  success: boolean;
  error: AuthError | null;
  errorCode?: AuthErrorCode;
  errorMessage?: string;
}

// --- NEW: Response Type for provider sign-in (redirect-based) ---
export interface ProviderSignInResponse {
  success: boolean;
  error: AuthError | null;
  errorCode?: AuthErrorCode;
  errorMessage?: string;
}

// --- NEW: Response Type for password reset request ---
export interface PasswordResetResponse {
  success: boolean;
  error: AuthError | null;
  errorCode?: AuthErrorCode;
  errorMessage?: string;
}

/**
 * A class-based service for handling Supabase authentication.
 * It must be instantiated with a SupabaseClient.
 */
export class AuthService {
  private supabase: SupabaseClient;

  constructor(supabaseClient: SupabaseClient) {
    if (!supabaseClient) {
      throw new Error('AuthService requires a valid SupabaseClient instance.');
    }
    this.supabase = supabaseClient;
  }

  // All methods now use `this.supabase`
  
  // --- NEW: Method for OAuth providers like Google ---
  signInWithProvider = async (
    provider: Provider,
    options?: { redirectTo?: string; scopes?: string }
  ): Promise<ProviderSignInResponse> => {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo:
          options?.redirectTo ?? (typeof window !== 'undefined' ? `${window.location.origin}/` : undefined),
        scopes: options?.scopes,
      },
    });
    if (error) {
      const mapped = mapAuthError(error);
      console.error(`OAuth error with provider ${provider}:`, error.message);
      return { success: false, error, errorCode: mapped.code, errorMessage: mapped.message };
    }
    return { success: true, error: null };
  };

  // --- NEW: Correct method for password reset ---
  resetPasswordForEmail = async (
    email: string,
    options?: { redirectTo?: string }
  ): Promise<PasswordResetResponse> => {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo:
        options?.redirectTo ?? (typeof window !== 'undefined' ? `${window.location.origin}/auth/update-password` : undefined),
    });
    if (error) {
      const mapped = mapAuthError(error);
      console.error('Password reset request error:', error.message);
      return { success: false, error, errorCode: mapped.code, errorMessage: mapped.message };
    }
    return { success: true, error: null };
  };

  signInWithOtp = async (
    email: string,
    options?: { emailRedirectTo?: string }
  ): Promise<AuthOperationResponse> => {
    const { data, error } = await this.supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo:
          options?.emailRedirectTo ?? (typeof window !== 'undefined' ? `${window.location.origin}/` : undefined),
      },
    });
    if (error) {
      const mapped = mapAuthError(error);
      console.error('Magic link OTP error:', error.message);
      return { success: false, error, errorCode: mapped.code, errorMessage: mapped.message };
    }
    return { success: true, data };
  };

  signInWithEmailPassword = async (email: string, password: string): Promise<AuthOperationResponse> => {
    const { data, error } = await this.supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const mapped = mapAuthError(error);
      console.error('Email/Password Sign In error:', error.message);
      return { success: false, error, errorCode: mapped.code, errorMessage: mapped.message };
    }
    return { success: true, data };
  };

  signUpWithEmailPassword = async (
    email: string,
    password: string,
    options?: { emailRedirectTo?: string }
  ): Promise<AuthOperationResponse> => {
    // Get the origin of the current window to build the redirect URL.
    // This makes it work seamlessly in development (localhost) and production.
    const redirectTo = options?.emailRedirectTo ?? (typeof window !== 'undefined' ? `${window.location.origin}/` : undefined);

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        // Provide the redirect URL. After the user clicks the link in their
        // email, Supabase will redirect them back to your app's root URL
        // with a session token in the URL fragment.
        emailRedirectTo: redirectTo,
      },
    });

    if (error) {
      const mapped = mapAuthError(error);
      console.error('Sign Up error:', error.message);
      return { success: false, error, errorCode: mapped.code, errorMessage: mapped.message };
    }
    return { success: true, data };
  };

  logout = async (): Promise<LogoutResponse> => {
    const { error } = await this.supabase.auth.signOut();
    if (error) {
      const mapped = mapAuthError(error);
      console.error('Logout error:', error.message);
      return { success: false, error, errorCode: mapped.code, errorMessage: mapped.message };
    }
    return { success: true, error: null };
  };

  onAuthStateChange = (
    callback: (event: AuthChangeEvent, session: Session | null) => void
  ): { data: { subscription: Subscription } } => {
    return this.supabase.auth.onAuthStateChange(callback);
  };

  getSession = async (): Promise<Session | null> => {
    const { data: { session }, error } = await this.supabase.auth.getSession();
    if (error) {
      console.error('Error fetching session:', error.message);
      return null;
    }
    return session;
  };

  getUser = async (): Promise<User | null> => {
    const { data: { user }, error } = await this.supabase.auth.getUser();
    if (error) {
      console.error('Error fetching current user:', error.message);
      return null;
    }
    return user;
  }
}
